import { Asset, MetadataValue } from '@/api/generatedApi';
import { HeaderText } from '@/components/HeaderText';
import { useListAllMetadataFields } from '@/hooks/api/metadata/useListAllMetadataFields';
import { Box, Divider, TableBody } from '@mui/material';
import { useRef } from 'react';
import { AssetTable } from '../AssetDetails/AssetTable';
import { getMetadataFieldType } from './getMetadataFieldType';
import { AddMetadataButton } from './MetadataAddButton';
import { MetadataFieldDisplay, MetadataTableRow } from './MetadataTableRow';

export const AssetMetadata = ({ asset }: { asset?: Asset }) => {
  const containerReference = useRef<HTMLDivElement>(null);
  const { metadataFields } = useListAllMetadataFields();

  const formattedMetadata = Object.entries(asset?.metadata ?? {})
    .map(([fieldName, fieldValue]) => {
      const field = metadataFields?.find(f => f.name === fieldName);
      return {
        fieldName,
        fieldValue,
        fieldType: getMetadataFieldType(fieldValue, field),
      };
    })
    .sort((a, b) => a.fieldName.localeCompare(b.fieldName, undefined, { sensitivity: 'base' }));

  const onMetadataChange = (newMetadata: Record<string, MetadataValue>) => {
    setTimeout(() => {
      const fieldName = Object.keys(newMetadata)[0];
      const targetElement = containerReference.current?.querySelector(
        `[data-field-name="${fieldName}"]`
      ) as HTMLElement;
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        targetElement.focus();
      }
    }, 100);
  };

  if (!asset) return;

  return (
    <>
      <Box display='flex' alignItems='center' margin='2px 8px 2px 0'>
        <HeaderText sx={{ margin: '4px 8px' }}>Metadata</HeaderText>
        <MetadataAction asset={asset} formattedMetadata={formattedMetadata} onMetadataChange={onMetadataChange} />
      </Box>

      {Object.keys(asset?.metadata ?? {}).length > 0 && <Divider />}
      <Box ref={containerReference} display='flex' flexDirection='column' overflow='auto'>
        <AssetTable size='small'>
          <TableBody>
            {formattedMetadata.map((field, index) => (
              <MetadataTableRow
                key={`${field.fieldName}-${index}`}
                field={field}
                asset={asset}
                onEditSuccess={onMetadataChange}
              />
            ))}
          </TableBody>
        </AssetTable>
      </Box>
    </>
  );
};

const MetadataAction = ({
  asset,
  formattedMetadata,
  onMetadataChange,
}: {
  asset?: Asset;
  formattedMetadata: MetadataFieldDisplay[];
  onMetadataChange: (newMetadata: Record<string, MetadataValue>) => void;
}) => {
  return (
    asset !== undefined && (
      <AddMetadataButton formattedMetadata={formattedMetadata} asset={asset} onMetadataChange={onMetadataChange} />
    )
  );
};
